import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle2, ShieldCheck, Briefcase, AlertTriangle, Settings, Clock } from 'lucide-react';
import SEO from '@/components/SEO';

const TermesConditions = () => {
    return (
        <>
            <SEO 
                title="Termes et Conditions"
                description="Conditions générales d'utilisation des services Orcsys. Découvrez nos termes et conditions pour une utilisation optimale de nos services."
                keywords="conditions générales, CGU, termes et conditions, utilisation, services, accord"
                type="legal"
            />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white pt-24">
                <div className="container px-4 mx-auto py-12">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12"
                        >
                            <div className="flex items-center gap-3 mb-8">
                                <FileText className="w-8 h-8 text-orcsys-500" />
                                <h1 className="text-4xl font-bold text-slate-900">
                                    Termes et Conditions
                                </h1>
                            </div>

                            <div className="prose prose-slate max-w-none">
                                <p className="text-slate-600 mb-8 text-lg">
                                    En utilisant notre site et nos services, vous acceptez d'être lié par les présentes conditions générales. 
                                    Veuillez les lire attentivement avant d'utiliser nos services.
                                </p>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <CheckCircle2 className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Acceptation des conditions
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            En accédant et en utilisant ce site, vous reconnaissez avoir pris connaissance des présentes conditions et vous vous engagez à les respecter. 
                                            Ces conditions constituent un accord juridiquement contraignant entre vous et Orcsys.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[140px] mt-1">Applicabilité :</span>
                                                    <span>Ces conditions s'appliquent à l'ensemble des services fournis par Orcsys, y compris le site web, les applications et les services connexes.</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[140px] mt-1">Mise à jour :</span>
                                                    <span>Orcsys se réserve le droit de modifier ces conditions à tout moment. Les modifications prennent effet dès leur publication.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Briefcase className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Services
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Orcsys propose une gamme complète de services de développement et d'automatisation, adaptés aux besoins spécifiques de chaque client.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6 mb-4">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-3">Nos engagements :</h3>
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[140px] mt-1">Qualité :</span>
                                                    <span>Nous nous engageons à fournir des services de haute qualité, conformes aux standards de l'industrie.</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[140px] mt-1">Support :</span>
                                                    <span>Une assistance technique est disponible pour répondre à vos questions et résoudre les problèmes éventuels.</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[140px] mt-1">Évolution :</span>
                                                    <span>Nos services sont régulièrement mis à jour pour intégrer les dernières technologies et innovations.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <ShieldCheck className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Utilisation du site
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            En utilisant notre site, vous vous engagez à respecter les conditions suivantes :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-4 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Ne pas utiliser le site d'une manière qui pourrait endommager, désactiver, surcharger ou altérer le site.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Ne pas tenter d'accéder à des zones ou fonctionnalités non autorisées du site.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Respecter toutes les lois et réglementations applicables lors de l'utilisation de nos services.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Ne pas utiliser le site pour des activités illégales ou non autorisées.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <AlertTriangle className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Limitation de responsabilité
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-4 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Orcsys ne peut être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le site.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Nous ne garantissons pas que le site sera exempt d'erreurs ou disponible de manière ininterrompue.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Les informations fournies sur le site le sont à titre indicatif et ne constituent pas un conseil professionnel.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Settings className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Modifications des services
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Orcsys se réserve le droit de :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-4 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Modifier ou arrêter temporairement ou définitivement tout ou partie des services avec ou sans préavis.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Ajouter ou supprimer des fonctionnalités selon l'évolution de nos services.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Imposer des limites à certaines fonctionnalités ou restreindre l'accès à certaines parties du site.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <div className="flex items-center gap-3 mb-4">
                                        <Clock className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Durée et résiliation
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Ces conditions restent en vigueur tant que vous utilisez nos services.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-4 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Orcsys peut résilier ou suspendre votre accès aux services immédiatement, sans préavis ni responsabilité, pour quelque raison que ce soit.</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>En cas de résiliation, les dispositions relatives à la propriété intellectuelle, aux garanties et aux limitations de responsabilité restent en vigueur.</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TermesConditions; 